import { useEffect, useState } from 'react';
import { useBranchStore } from '@/stores';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { TrendingDown, Package, Store, CreditCard, Banknote, CheckCircle2, Clock, Search, Wallet } from 'lucide-react';
import { cur, RiyalIcon } from '@/lib/currency';
import { ExportButtons } from '@/components/ExportButtons';
import BranchLink from '@/components/BranchLink';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday, toLocalYmd } from '@/lib/dateRange';

const SR = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PurchasesSummaryPage() {
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(toLocalYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [endDate, setEndDate] = useState(localToday());
  const posBranch = useBranchStore((s) => s.activeBranchId);
  const setPosBranch = useBranchStore((s) => s.setActiveBranchId);
  const [branchId, setBranchId] = useState(posBranch || 'all');
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter962 — Follow the shared POS/Stock active branch (branchStore).
  useEffect(() => { setBranchId(posBranch || 'all'); }, [posBranch]);
  // iter395o — Per-bill table filters (search by supplier/description and
  // by mode). Keeps the table responsive on screens with hundreds of bills.
  const [billSearch, setBillSearch] = useState('');
  const [billMode, setBillMode] = useState('all');
  // iter395p — "Pay this bill" inline action. State holds the bill row
  // being paid + the form values. Calls /suppliers/{id}/pay-credit
  // with allocations targeting just this bill.
  const [payingBill, setPayingBill] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMode, setPayMode] = useState('cash');
  const [paySubmitting, setPaySubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (branchId && branchId !== 'all') params.set('branch_id', branchId);
      const res = await api.get(`/accounting/purchases-summary?${params.toString()}`);
      setData(res.data);
    } catch { toast.error('Failed to load purchases summary'); }
    finally { setLoading(false); }
  };

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [branchId]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="purchases-summary-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Package size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Purchases Summary</h1>
              <p className="text-xs text-muted-foreground">Supplier bills + supplier payments, grouped by branch / supplier / mode</p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); setPosBranch(v === 'all' ? null : v); }}>
              <SelectTrigger className="w-44 h-9 text-xs" data-testid="ps-branch-filter"><SelectValue placeholder="All Branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-36" />
            <span className="text-muted-foreground">to</span>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-36" />
            <Button size="sm" onClick={fetchData} data-testid="ps-generate-btn">Apply</Button>
            <ExportButtons branchIds={branchId && branchId !== 'all' ? [branchId] : []} dataType="purchases_summary" filters={{ branch_id: branchId === 'all' ? null : branchId }} startDate={startDate} endDate={endDate} testid="ps-export" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]"><div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <MiniKPI label="Total Bills" value={SR(data.totals.gross)} icon={TrendingDown} color="amber" testid="ps-kpi-gross" />
              <MiniKPI label="# Bills" value={data.totals.count} icon={Package} color="info" testid="ps-kpi-count" />
              <MiniKPI label="Avg. Bill" value={SR(data.totals.avg_bill)} icon={CreditCard} color="purple" testid="ps-kpi-avg" />
              {/* iter395o — Paid + Outstanding KPIs surface the new
                  per-bill balance roll-up returned by the backend. */}
              <MiniKPI label="Paid" value={SR(data.totals.paid || 0)} icon={CheckCircle2} color="success" testid="ps-kpi-paid" />
              <MiniKPI label="Outstanding" value={SR(data.totals.outstanding || 0)} icon={Clock} color="rose" testid="ps-kpi-outstanding" />
              <MiniKPI label="Supplier Payments" value={SR(data.supplier_payments_made.total)} icon={Banknote} color="success" testid="ps-kpi-sp" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Store size={14} /> By Branch</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-muted/50"><th className="text-left p-2">Branch</th><th className="text-right p-2">Bills</th><th className="text-right p-2">Total</th></tr></thead>
                    <tbody>
                      {data.by_branch.map(r => (
                        <tr key={r.branch_id} className="border-t"><td className="p-2"><BranchLink branchId={r.branch_id} name={r.branch_name} /></td><td className="p-2 text-right">{r.count}</td><td className="p-2 text-right font-mono font-semibold text-error">{SR(r.total)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CreditCard size={14} /> By Payment Mode</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-muted/50"><th className="text-left p-2">Mode</th><th className="text-right p-2">Total</th><th className="text-right p-2">%</th></tr></thead>
                    <tbody>
                      {data.by_payment_mode.map(r => {
                        const pct = data.totals.gross > 0 ? (r.total / data.totals.gross * 100) : 0;
                        return <tr key={r.mode} className="border-t"><td className="p-2 capitalize">{r.mode.replace('_', ' ')}</td><td className="p-2 text-right font-mono">{SR(r.total)}</td><td className="p-2 text-right text-muted-foreground">{pct.toFixed(1)}%</td></tr>;
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Package size={14} /> Top 10 Suppliers</CardTitle></CardHeader>
              <CardContent className="p-0">
                {data.by_supplier_top10.length === 0 ? (
                  <p className="p-6 text-center text-xs text-muted-foreground">No supplier-linked bills in this period.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead><tr className="bg-muted/50"><th className="text-left p-2">#</th><th className="text-left p-2">Supplier</th><th className="text-right p-2">Bills</th><th className="text-right p-2">Total</th></tr></thead>
                    <tbody>
                      {data.by_supplier_top10.map((r, i) => (
                        <tr key={r.supplier_id} className="border-t"><td className="p-2 text-muted-foreground">{i + 1}</td><td className="p-2 font-medium">{r.supplier_name}</td><td className="p-2 text-right">{r.count}</td><td className="p-2 text-right font-mono font-semibold">{SR(r.total)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* iter395o — Full per-bill table. Replaces the user complaint
                "let us see all purchase bills with supplier name and others".
                Includes search + payment-mode filter so it stays usable
                on months with 100s of bills. */}
            {(data.bills?.length || 0) > 0 && (() => {
              const q = billSearch.trim().toLowerCase();
              const filtered = (data.bills || []).filter((b) => {
                if (billMode !== 'all' && (b.payment_mode || '').toLowerCase() !== billMode) return false;
                if (!q) return true;
                return (
                  (b.supplier_name || '').toLowerCase().includes(q)
                  || (b.description || '').toLowerCase().includes(q)
                  || (b.branch_name || '').toLowerCase().includes(q)
                  || (b.reference || '').toLowerCase().includes(q)
                );
              });
              const tot = filtered.reduce((acc, b) => {
                acc.amount += b.amount || 0;
                acc.paid += b.paid || 0;
                acc.remaining += b.remaining || 0;
                return acc;
              }, { amount: 0, paid: 0, remaining: 0 });
              return (
                <Card data-testid="ps-bills-table-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
                      <Package size={14} />
                      All Purchase Bills <span className="text-muted-foreground text-xs font-normal">({filtered.length} of {data.bills.length})</span>
                      <div className="ml-auto flex items-center gap-2">
                        <div className="relative">
                          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            value={billSearch}
                            onChange={(e) => setBillSearch(e.target.value)}
                            placeholder="Filter by supplier / branch / description…"
                            className="h-7 pl-7 text-xs w-72"
                            data-testid="ps-bills-search"
                          />
                        </div>
                        <Select value={billMode} onValueChange={setBillMode}>
                          <SelectTrigger className="w-32 h-7 text-xs" data-testid="ps-bills-mode-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All modes</SelectItem>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="bank">Bank</SelectItem>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="credit">Credit</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {filtered.length === 0 ? (
                      <p className="p-6 text-center text-xs text-muted-foreground">No bills match the filter.</p>
                    ) : (
                      <div className="overflow-x-auto max-h-[60dvh] overflow-y-auto">
                        <table className="w-full text-xs" data-testid="ps-bills-table">
                          <thead className="bg-muted/50 text-[10px] uppercase sticky top-0">
                            <tr>
                              <th className="text-left p-2">Date</th>
                              <th className="text-left p-2">Supplier</th>
                              <th className="text-left p-2">Branch</th>
                              <th className="text-left p-2">Description</th>
                              <th className="text-left p-2">Mode</th>
                              <th className="text-right p-2">Amount</th>
                              <th className="text-right p-2">Paid</th>
                              <th className="text-right p-2">Remaining</th>
                              <th className="text-right p-2 w-20">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((b) => (
                              <tr key={b.id} className="border-t hover:bg-muted/30">
                                <td className="p-2 text-muted-foreground whitespace-nowrap">{b.date || '—'}</td>
                                <td className="p-2 font-medium">{b.supplier_name}</td>
                                <td className="p-2"><BranchLink branchId={b.branch_id} name={b.branch_name} /></td>
                                <td className="p-2 text-muted-foreground truncate max-w-[260px]" title={b.description}>{b.description || '—'}</td>
                                <td className="p-2 capitalize text-[10px]">{(b.payment_mode || '—').replace('_', ' ')}</td>
                                <td className="p-2 text-right font-mono">{SR(b.amount)}</td>
                                <td className="p-2 text-right font-mono text-success">{SR(b.paid)}</td>
                                <td className={`p-2 text-right font-mono font-semibold ${b.remaining > 0 ? 'text-rose-700' : 'text-muted-foreground'}`}>{SR(b.remaining)}</td>
                                <td className="p-2 text-right">
                                  {/* iter395p — "Pay this bill" inline button.
                                      Only enabled for bills with remaining > 0
                                      AND a real supplier_id (some legacy bills
                                      have no supplier — those need the
                                      Suppliers page Pay Credit dialog). */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-[11px] px-2 disabled:opacity-40"
                                    disabled={!(b.remaining > 0 && b.supplier_id)}
                                    onClick={() => {
                                      setPayingBill(b);
                                      setPayAmount(String(b.remaining || 0));
                                      setPayMode('cash');
                                    }}
                                    data-testid={`ps-bill-pay-${b.id}`}
                                    title={!b.supplier_id ? 'No supplier — open the Suppliers page to settle' : (b.remaining > 0 ? 'Settle this bill from cash/bank' : 'Already paid')}
                                  >
                                    <Wallet size={11} className="mr-1" /> Pay
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-muted/30 sticky bottom-0">
                            <tr className="border-t font-semibold">
                              <td colSpan={5} className="p-2 text-right">Filtered total ({filtered.length} bills)</td>
                              <td className="p-2 text-right font-mono">{SR(tot.amount)}</td>
                              <td className="p-2 text-right font-mono text-success">{SR(tot.paid)}</td>
                              <td className="p-2 text-right font-mono text-rose-700">{SR(tot.remaining)}</td>
                              <td className="p-2"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}
          </>
        )}
      </div>

      {/* iter395p — Pay this bill dialog. Uses the existing
          /suppliers/{id}/pay-credit endpoint with allocations targeting
          just this bill, so the supplier ledger updates correctly and
          the bill's `paid` recomputes to (amount - remaining + paidNow). */}
      {payingBill && (
        <PayBillDialog
          bill={payingBill}
          amount={payAmount}
          mode={payMode}
          submitting={paySubmitting}
          onAmountChange={setPayAmount}
          onModeChange={setPayMode}
          onClose={() => setPayingBill(null)}
          onPay={async () => {
            const amt = parseFloat(payAmount || '0');
            if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
            if (amt > payingBill.remaining + 0.01) { toast.error(`Amount exceeds remaining (${SR(payingBill.remaining)})`); return; }
            setPaySubmitting(true);
            try {
              await api.post(`/suppliers/${payingBill.supplier_id}/pay-credit`, {
                amount: amt,
                payment_mode: payMode,
                branch_id: payingBill.branch_id || null,
                date: new Date().toISOString(),
                notes: `Settlement for bill ${payingBill.reference || payingBill.id?.slice(0, 8) || ''}`.trim(),
                allocations: [{ expense_id: payingBill.id, amount: amt }],
              });
              toast.success(`Paid ${cur()} ${amt.toFixed(2)} to ${payingBill.supplier_name}`);
              setPayingBill(null);
              fetchData();
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Failed to record payment');
            } finally { setPaySubmitting(false); }
          }}
        />
      )}
    </DashboardLayout>
  );
}

// iter395p — Pay-this-bill dialog rendered inline at the bottom of
// PurchasesSummaryPage. Self-contained for clarity since this page
// otherwise has no dialogs.
function PayBillDialog({ bill, amount, mode, submitting, onAmountChange, onModeChange, onClose, onPay }) {
  return (
    <Dialog open={!!bill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ps-pay-dialog">
        <DialogHeader>
          <DialogTitle>Pay this bill</DialogTitle>
          <DialogDescription>
            Settle the outstanding amount on this purchase bill.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-lg border border-border p-3 bg-muted/30 text-sm">
            <div className="font-medium">{bill.supplier_name}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{bill.description || bill.reference || ''}</div>
            <div className="grid grid-cols-3 gap-3 mt-2 text-[11px]">
              <div><div className="text-muted-foreground">Amount</div><div className="font-mono font-semibold">{SR(bill.amount)}</div></div>
              <div><div className="text-muted-foreground">Paid</div><div className="font-mono font-semibold text-success">{SR(bill.paid)}</div></div>
              <div><div className="text-muted-foreground">Remaining</div><div className="font-mono font-bold text-rose-700">{SR(bill.remaining)}</div></div>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Payment amount</label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => onAmountChange(e.target.value)}
              placeholder="0.00"
              className="mt-1"
              data-testid="ps-pay-amount"
            />
            <button
              type="button"
              onClick={() => onAmountChange(String(bill.remaining))}
              className="text-[10px] text-violet-700 hover:underline mt-1"
              data-testid="ps-pay-fullamount"
            >
              Use full remaining ({SR(bill.remaining)})
            </button>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Payment mode</label>
            <Select value={mode} onValueChange={onModeChange}>
              <SelectTrigger className="mt-1" data-testid="ps-pay-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
                <SelectItem value="online">Online</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="ps-pay-cancel">Cancel</Button>
          <Button onClick={onPay} disabled={submitting} data-testid="ps-pay-confirm">
            {submitting ? 'Recording…' : 'Pay now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MiniKPI = ({ label, value, icon: Icon, color, testid }) => {
  const bg = { amber: 'from-amber-50 to-orange-50 border-amber-200', info: 'from-blue-50 to-cyan-50 border-blue-200', purple: 'from-violet-50 to-purple-50 border-violet-200', success: 'from-emerald-50 to-teal-50 border-emerald-200', rose: 'from-rose-50 to-red-50 border-rose-200' }[color] || '';
  return (
    <Card className={`border bg-gradient-to-br ${bg}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2"><Icon size={16} className="text-muted-foreground" /></div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <p className="text-xl font-bold mt-1 inline-flex items-center gap-1">{typeof value === 'number' ? value : <><RiyalIcon size="0.7em" /> {value}</>}</p>
      </CardContent>
    </Card>
  );
};
